/**
 * ReachModal — DEV-1408.
 *
 * The reach ladder (DEV-1402) relocated out of the About tab and into a modal.
 * A distribution CONTROL doesn't belong inline in descriptive content; it surfaces
 * on demand — opened by the Publish "you're live, go further?" moment and by the
 * persistent header status chip thereafter.
 *
 * Title: "How far this reaches." Body = the existing ChronicleReachLadder, reused
 * as-is (its own internal title is dropped here to avoid a duplicated heading).
 * Opaque surface (composite --bg-primary + --bg-elevated); closes on backdrop / X /
 * Esc; stable height (no resize when the ladder's content changes).
 */

import { useEffect } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import ChronicleReachLadder from '../ChronicleReachLadder/ChronicleReachLadder';
import type { ChronicleReach } from '../../api/chronicles';

interface Props {
  chronicleGuid: string;
  isPublished: boolean;
  reach: ChronicleReach | null | undefined;
  /** Toggle publish ⇄ draft (DEV-1409 — relocated from the header into the
   *  modal's Visibility zone). Publishing happens inside the modal; the modal
   *  stays open afterward so there's no jarring close/reopen. */
  onTogglePublish: () => void;
  /** True while the publish/draft toggle is in flight. */
  togglingPublish: boolean;
  onSubmitForExpertReview: () => void;
  submittingExpert: boolean;
  canSubmitForExpertReview: boolean;
  /** DEV-1396 — whether the content has ≥1 subject tag. Gates the expert submit. */
  hasSubjects: boolean;
  onCommunitySubmitted: () => void;
  onClose: () => void;
}

export default function ReachModal({
  chronicleGuid,
  isPublished,
  reach,
  onTogglePublish,
  togglingPublish,
  onSubmitForExpertReview,
  submittingExpert,
  canSubmitForExpertReview,
  hasSubjects,
  onCommunitySubmitted,
  onClose,
}: Props) {
  const { t } = useAppTranslation('pages');

  // Close on Esc.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  };
  // Opaque surface — composite a solid base under the elevated tint (CLAUDE.md rule).
  const card: React.CSSProperties = {
    width: 'min(620px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', borderRadius: 12,
    border: '1px solid var(--border-default)', backgroundColor: 'var(--bg-primary)',
    backgroundImage: 'linear-gradient(var(--bg-elevated), var(--bg-elevated))', boxShadow: 'var(--shadow-lg)',
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: '16px 18px', borderBlockEnd: '1px solid var(--border-subtle)' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, color: 'var(--text-primary)' }}>{t('chronicleView.reach.title')}</h2>
            <div style={{ marginBlockStart: 4, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
              {t('chronicleView.reach.modalSubtitle')}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('chronicleView.reach.close')}
            style={{ background: 'transparent', border: 0, color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer', lineHeight: 1, flex: '0 0 auto' }}
          >
            ×
          </button>
        </div>

        {/* Body — Visibility zone (DEV-1409) above the reach ladder. Scrolls within the fixed shell. */}
        <div style={{ padding: 18, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* ── Visibility zone (DEV-1409) — the existence control, relocated out
              of the header. A plain current-state line + exactly one explicit
              action whose label says what it DOES, never the ambiguous toggle. ── */}
          <section
            style={{
              display: 'flex', flexDirection: 'column', gap: 10,
              padding: '14px 16px', borderRadius: 10,
              background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
              {t('chronicleView.visibility.title')}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              {isPublished
                ? t('chronicleView.visibility.statePublished')
                : t('chronicleView.visibility.stateDraft')}
            </div>
            <button
              type="button"
              onClick={onTogglePublish}
              disabled={togglingPublish}
              style={{
                alignSelf: 'flex-start',
                padding: '6px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13,
                cursor: togglingPublish ? 'default' : 'pointer', opacity: togglingPublish ? 0.7 : 1,
                border: isPublished ? '1px solid var(--border-default)' : 'none',
                background: isPublished ? 'transparent' : 'var(--color-success)',
                color: isPublished ? 'var(--text-secondary)' : 'var(--text-on-accent, #fff)',
              }}
            >
              {togglingPublish
                ? '…'
                : isPublished
                  ? t('chronicleView.visibility.moveToDraft')
                  : t('chronicleView.visibility.publish')}
            </button>
            {isPublished && (
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
                {t('chronicleView.visibility.moveToDraftConsequence')}
              </div>
            )}
          </section>

          <ChronicleReachLadder
            chronicleGuid={chronicleGuid}
            isPublished={isPublished}
            reach={reach}
            onSubmitForExpertReview={onSubmitForExpertReview}
            submittingExpert={submittingExpert}
            canSubmitForExpertReview={canSubmitForExpertReview}
            hasSubjects={hasSubjects}
            onCommunitySubmitted={onCommunitySubmitted}
            hideTitle
          />
        </div>
      </div>
    </div>
  );
}
